import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { OptimizedImage } from '@/components/OptimizedImage';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { LessonSidebar } from '@/components/learn/LessonSidebar';
import { DifficultyBadge } from '@/components/learn/DifficultyBadge';
import { ArrowLeft, ArrowRight, Clock, BookOpen, Target, MapPin, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getLessonBySlug, getAllLessons, getAdjacentLessons } from '@/lib/learn';

const LearnLesson: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const lesson = slug ? getLessonBySlug(slug) : undefined;
  const allLessons = getAllLessons();

  if (!lesson) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="max-w-3xl mx-auto mt-8 text-center">
            <h1 className="text-3xl font-bold mb-4 text-foreground">Lesson Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The lesson you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/intro-to-astrophotography">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Learn
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { prev, next } = getAdjacentLessons(lesson.slug);

  const DIFFICULTY_TO_LEVEL: Record<string, string> = {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  };

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": lesson.title,
    "description": lesson.seo_description || lesson.excerpt,
    "image": lesson.featured_image || undefined,
    "educationalLevel": DIFFICULTY_TO_LEVEL[lesson.difficulty] || 'Beginner',
    "learningResourceType": "lesson",
    "position": lesson.order,
    "isPartOf": {
      "@type": "Course",
      "name": "Intro to Astrophotography",
      "url": "https://www.milkywayplanner.com/intro-to-astrophotography"
    },
    "author": {
      "@type": "Person",
      "name": "Eric D. Brown, D.Sc.",
      "url": "https://ericbrown.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Milky Way Planner",
      "url": "https://www.milkywayplanner.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.milkywayplanner.com/intro-to-astrophotography/${lesson.slug}`
    }
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.milkywayplanner.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Learn",
        "item": "https://www.milkywayplanner.com/intro-to-astrophotography"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": lesson.title,
        "item": `https://www.milkywayplanner.com/intro-to-astrophotography/${lesson.slug}`
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={lesson.seo_title || `${lesson.title} | Learn Astrophotography`}
        description={lesson.seo_description || lesson.excerpt}
        keywords={lesson.seo_keywords || undefined}
        canonicalUrl={`/intro-to-astrophotography/${lesson.slug}`}
        ogImage={lesson.featured_image || undefined}
        isArticle={true}
        structuredData={[articleStructuredData, breadcrumbStructuredData]}
      />

      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex gap-10">
            {/* Sidebar — hidden on mobile */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <LessonSidebar lessons={allLessons} currentSlug={lesson.slug} />
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0 max-w-3xl">
              <Link to="/intro-to-astrophotography">
                <Button variant="outline" size="sm" className="mb-6">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Learn
                </Button>
              </Link>

              <article>
                {/* Header */}
                <header className="mb-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <BookOpen className="w-3.5 h-3.5" />
                      Lesson {lesson.order} of {allLessons.length}
                    </span>
                    <DifficultyBadge difficulty={lesson.difficulty} />
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {lesson.reading_time} min read
                    </span>
                  </div>

                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-6 leading-tight">
                    {lesson.title}
                  </h1>

                  {lesson.featured_image && (
                    <div className="aspect-video mb-8 rounded-lg overflow-hidden">
                      <OptimizedImage
                        src={lesson.featured_image}
                        alt={lesson.title}
                        width={1080}
                        quality={80}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {lesson.excerpt && (
                    <p className="text-lg text-muted-foreground leading-relaxed border-l-4 border-gold pl-6">
                      {lesson.excerpt}
                    </p>
                  )}
                </header>

                {/* Content */}
                <div className="prose prose-lg prose-slate dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-code:text-foreground prose-blockquote:text-muted-foreground prose-img:rounded-lg prose-img:shadow-lg prose-figcaption:text-center prose-figcaption:italic prose-figcaption:text-muted-foreground prose-figcaption:text-sm prose-figcaption:mt-2">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      img: ({ src, alt, ...props }) =>
                        src ? (
                          <OptimizedImage
                            src={src}
                            alt={alt || ''}
                            width={1080}
                            quality={80}
                            className="w-full rounded-lg shadow-lg"
                            {...props}
                          />
                        ) : null,
                    }}
                  >
                    {lesson.content}
                  </ReactMarkdown>
                </div>
              </article>

              {/* Prev / Next navigation */}
              <div className="mt-14 border-t border-border pt-8">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  {prev ? (
                    <Link
                      to={`/intro-to-astrophotography/${prev.slug}`}
                      className="group flex-1 p-4 rounded-lg border border-border hover:border-gold/30 hover:bg-gold/5 transition-colors"
                    >
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">
                        Previous
                      </span>
                      <div className="flex items-center gap-2 mt-1">
                        <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors flex-shrink-0" />
                        <span className="font-medium text-foreground group-hover:text-gold transition-colors line-clamp-1">
                          {prev.title}
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex-1" />
                  )}
                  {next ? (
                    <Link
                      to={`/intro-to-astrophotography/${next.slug}`}
                      className="group flex-1 p-4 rounded-lg border border-border hover:border-gold/30 hover:bg-gold/5 transition-colors text-right"
                    >
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">
                        Next
                      </span>
                      <div className="flex items-center justify-end gap-2 mt-1">
                        <span className="font-medium text-foreground group-hover:text-gold transition-colors line-clamp-1">
                          {next.title}
                        </span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors flex-shrink-0" />
                      </div>
                    </Link>
                  ) : (
                    <div className="flex-1" />
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* CTA strip — full width below sidebar + content */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/planner" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Plan Your Shoot</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Free Milky Way calendar with nightly visibility ratings.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Open Planner <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link to="/dark-sky-locations" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Find Dark Skies</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                400+ verified dark sky locations with Bortle ratings.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Browse Locations <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            {(!user || !isSubscribed) ? (
              <Link to="/register" className="group p-6 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-foreground">Go Premium</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Moon Planner, Trip Planner, PDF exports, and weather forecasts.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-dark transition-colors inline-flex items-center gap-1">
                  Start Premium <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ) : (
              <Link to="/dashboard" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-white">Your Planning Hub</h4>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-3">
                  Moon Planner, Trip Planner, saved locations, and PDF exports.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  Open Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LearnLesson;
